import { Fab, lighten, styled, useTheme } from '@mui/material'
import { Intersect } from '@phosphor-icons/react'
import { ApplicationTypes, type Hole } from 'metalworks-domain'
import { ToolState } from 'metalworks-domain/src/__generated/application-types'
import { useObservableEagerState } from 'observable-hooks'
import { useCallback } from 'react'

import { SelectionHotspot } from '@salsita-ai/platform-project-sdk/configurator'
import { TooltipWithArrow, SelectionHotspot as UiSelectionHotspot } from '@salsita-ai/platform-project-sdk/ui'

import { anchorPointsService } from '../systems/anchor-points-service'
import { configuratorInterface } from '../systems/configurator-interface'

const SelectedHotspot = styled(UiSelectionHotspot)(({ theme }) => ({
  width: 20,
  height: 20,
  borderColor: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.main,
}))

const ConflictIcon = (props: { entityId: Hole['id'] }) => {
  const { entityId } = props
  const theme = useTheme()
  const { navigationSystem, storeSystem, domain } = configuratorInterface.useDomain()

  const $plate = configuratorInterface.useRenderedSelector(({ state }) => domain.getRoot$Entity(state), [])

  const handleClick = useCallback(() => {
    storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.FeatureList)])
  }, [navigationSystem, storeSystem])

  const isFeatureOverlapping = $plate.$allOverlappingFeatureIds.includes(entityId)
  if (!isFeatureOverlapping) {
    return null
  }

  return (
    <TooltipWithArrow title="This feature is overlapping" placement="top" enterDelay={300}>
      <Fab
        size="small"
        onClick={handleClick}
        sx={{
          position: 'absolute',
          left: 20,
          bottom: 20,
          backgroundColor: 'error.main',
          '&:hover': { backgroundColor: lighten(theme.palette.error.dark, 0.5) },
        }}
      >
        <Intersect size={26} color="white" />
      </Fab>
    </TooltipWithArrow>
  )
}

interface Props {
  id: Hole['id']
}

/**
 * A custom selection hotspot that provides a visual indication when a Hole entity
 * is selected (because the standard highlight system will not work for holes).
 */
export const FeatureSelectionHotspot = (props: Props) => {
  const { id } = props
  const { rootEntityId, navigationSystem } = configuratorInterface.useDomain()

  const { visible: anchorPointsVisible } = useObservableEagerState(anchorPointsService.state$)

  const selectedEntityId = configuratorInterface.useSelectedDomainEntityId()

  const isAnyFeatureSelected = selectedEntityId !== rootEntityId
  const isSelected = isAnyFeatureSelected && selectedEntityId === id

  const toolState = useObservableEagerState(
    configuratorInterface.useStoreSelector$(emission => navigationSystem.getActiveToolState(emission.state), [])
  )

  if ((isAnyFeatureSelected && anchorPointsVisible) || toolState !== ToolState.Configure) {
    return null
  }

  return (
    <>
      {isSelected ? <SelectedHotspot /> : <SelectionHotspot id={id} />}
      <ConflictIcon entityId={id} />
    </>
  )
}
